import { useMemo } from 'react';
import { useWorkspaceStore } from '../stores/workspaceStore';
import { roleHasPermission, getPermissionsForRole } from '@mellify/shared';
import type { Permission } from '@mellify/shared';

/**
 * usePermission
 *
 * Check whether the current user has a specific permission
 * in the active workspace based on their role.
 *
 * @param permission - The permission to check
 * @returns boolean indicating whether the user has the permission
 *
 * @example
 * ```ts
 * const canManageTeam = usePermission('manage_team');
 * const canManageAds = usePermission('manage_ads');
 * const canViewAdInsights = usePermission('view_ad_insights');
 *
 * if (!canManageTeam) {
 *   return <p>You do not have permission to manage team members.</p>;
 * }
 * ```
 */
export function usePermission(permission: Permission): boolean {
  const currentMemberRole = useWorkspaceStore((state) => state.currentMemberRole);

  return useMemo(() => {
    if (!currentMemberRole) return false;
    return roleHasPermission(currentMemberRole, permission);
  }, [currentMemberRole, permission]);
}

/**
 * usePermissions
 *
 * Check multiple permissions at once. Returns an object
 * mapping each permission to a boolean.
 *
 * @param permissions - Array of permissions to check
 * @returns Record mapping each permission to its check result
 *
 * @example
 * ```ts
 * const perms = usePermissions(['manage_team', 'manage_settings', 'manage_ads']);
 * if (perms.manage_team) { ... }
 * if (perms.manage_ads) { ... }
 * ```
 */
export function usePermissions<T extends Permission>(
  permissions: T[]
): Record<T, boolean> {
  const currentMemberRole = useWorkspaceStore((state) => state.currentMemberRole);

  return useMemo(() => {
    const result = {} as Record<T, boolean>;
    for (const perm of permissions) {
      result[perm] = currentMemberRole ? roleHasPermission(currentMemberRole, perm) : false;
    }
    return result;
  }, [currentMemberRole, permissions]);
}

/**
 * useCurrentPermissions
 *
 * Get the full list of permissions the current user has
 * in the active workspace.
 *
 * @returns Array of permissions granted to the current role
 *
 * @example
 * ```ts
 * const permissions = useCurrentPermissions();
 * // ['manage_team', 'manage_channels', 'manage_ads', ...]
 * ```
 */
export function useCurrentPermissions(): Permission[] {
  const currentMemberRole = useWorkspaceStore((state) => state.currentMemberRole);

  return useMemo(() => {
    if (!currentMemberRole) return [];
    return getPermissionsForRole(currentMemberRole);
  }, [currentMemberRole]);
}